import { TaskState, type UserInfo } from "@iflyrpa/share";
import type { Response } from "playwright-core";
import type { SteelAction } from "./index";

const encode_mnsv2 = require("../../utils/xhs_ob/x_s.encoder.js") as (
    mnsv2: string,
    platform: string,
    req_type: string,
) => string;

import { ExecutionState } from "../../types";
import { ProxyAgent } from "../../utils/proxy";

const scanRetryMaxCount = 60; // 二维码扫描重试
// 等待二维码验证结果最长时间
const waitQrcodeResultMaxTime = scanRetryMaxCount * 2 * 1000;

interface CookieLike {
    name: string;
    value: string;
}

/**
 * 收集浏览器上下文 cookie，并确保关键的 a1 设备指纹 cookie 存在。
 *
 * a1 由小红书主站在首次访问 www.xiaohongshu.com 时通过 Set-Cookie 种下，整个请求
 * 签名体系都依赖它。少数情况下登录成功瞬间抓取时 a1 尚未落地，会导致后续 session
 * 检测因「缺失关键 cookie a1」直接失败且无法补救。这里在抓取阶段做一次补救：
 * 若首次抓取缺 a1，则显式访问主站等待 a1 落地后重新抓取。
 *
 * @returns 抓取到的 cookies；调用方需自行判断其中是否含 a1。
 */
async function collectCookiesWithA1(task: any): Promise<CookieLike[]> {
    const hasA1 = (cookies: CookieLike[]) => cookies.some((c) => c.name === "a1" && !!c.value);

    let cookies: CookieLike[] = (await task.steelBrowserContext?.cookies()) ?? [];
    if (hasA1(cookies)) return cookies;

    task.logger?.warn("[XhsLogin] 首次抓取缺失 a1，尝试补救……");

    try {
        const page = task.steelBrowserContext?.pages?.()[0];
        if (page) {
            await page.goto("https://www.xiaohongshu.com", { waitUntil: "domcontentloaded", timeout: 15000 });
            // a1 通过 Set-Cookie 异步写入 context，给予短暂等待窗口轮询。
            for (let i = 0; i < 10; i++) {
                cookies = (await task.steelBrowserContext?.cookies()) ?? [];
                if (hasA1(cookies)) {
                    task.logger?.info(`[XhsLogin] a1 补救成功，第 ${i + 1} 次轮询命中`);
                    return cookies;
                }
                await new Promise((resolve) => setTimeout(resolve, 500));
            }
        }
    } catch (err) {
        task.logger?.warn("[XhsLogin] a1 补救访问主站失败", err);
    }

    // 返回最后一次抓取结果（可能仍缺 a1，由调用方判定）
    cookies = (await task.steelBrowserContext?.cookies()) ?? [];
    return cookies;
}

interface InitialState {
    user?: {
        userInfo?: {
            _rawValue?: {
                id: number;
                nickname: string;
                images?: string;
                userId?: string;
            };
        };
    };
}

export const rpaServer: SteelAction = async (task: any, params: any) => {
    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");
    const reporter = task.reportService?.reportLoginStatus ?? (() => Promise.resolve());
    let executionState: ExecutionState | undefined;

    let proxyUrl: string | undefined;
    if (params.proxyLoc) {
        task.logger?.info(`==> 开始获取代理信息：${params}`);
        const ProxyAgentResult = await ProxyAgent({ logger: task.logger }, params.proxyLoc, params.accountId);
        task.logger?.info("==> 代理信息获取成功！");
        proxyUrl = ProxyAgentResult ? `http://${ProxyAgentResult.ip}:${ProxyAgentResult.port}` : undefined;
    }

    const page = await task.createPage({
        url: "https://www.xiaohongshu.com/login",
        proxyUrl,
        userAgent: params.userAgent,
    });

    task.steelBrowser?.on("disconnected", () => {
        executionState = ExecutionState.BROWSER_CLOSED;
    });

    await updateTaskState?.({
        state: TaskState.WAIT_SCAN,
        connectAddress: task.steelConnector?.getProxyUrl(task.sessionId || "", "v1/sessions/debug"),
        sessionId: task.sessionId,
    });

    let userInfo: UserInfo = { uniqueId: "", avatar: "", name: "" };
    await new Promise<void>((resolve, reject) => {
        let finished = false;

        const timer = setTimeout(async () => {
            if (finished) return; // 检查是否已经完成
            cleanup();
            executionState = ExecutionState.TIMEOUT;
            await updateTaskState?.({
                state: TaskState.TIMEOUT,
                error: "等待扫码登录超时",
            });
            resolve();
        }, waitQrcodeResultMaxTime);

        const cleanup = () => {
            if (finished) return; // 防止多次调用
            finished = true;
            clearTimeout(timer);
            page.off("response", handleWebCommon);
            page.off("response", handleCheckQR);
            task.steelBrowser?.off("disconnected", handleBrowserClosed);
        };

        const handleBrowserClosed = () => {
            cleanup();
            executionState = ExecutionState.BROWSER_CLOSED;
            resolve();
        };

        const handleWebCommon = async (resp: Response) => {
            try {
                const url = resp.url();

                // 登录成功后会触发 homefeed 请求
                if (url.includes("/explore") && resp.request().method() === "GET" && resp.status() === 200) {
                    cleanup();
                    await updateTaskState?.({
                        state: TaskState.CONFIRMED,
                    });

                    await page.waitForFunction(
                        () => typeof (window as unknown as { mnsv2: unknown }).mnsv2 === "function",
                        {
                            timeout: 10000,
                        },
                    );
                    const ticket_mnsv2 = await page.evaluate(
                        // 调用浏览器环境获取加密参数
                        ({ c, d }: { c: string; d: string }) => {
                            if (typeof (window as unknown as { mnsv2: unknown }).mnsv2 === "function") {
                                return (window as unknown as { mnsv2: (...args: unknown[]) => string }).mnsv2(c, d);
                            }
                            return "mnsv2 未定义";
                        },
                        {
                            c: '/api/cas/customer/web/service-ticket{"service":"https://creator.xiaohongshu.com","source":"official","type":"tgt"}',
                            d: "71bcd9dc89f6c256ead6cbe2b0a9793c", // md5
                        },
                    );

                    const XS = encode_mnsv2(ticket_mnsv2, "Windows", "object");

                    const createrLogin = await page.request.post(
                        "https://customer.xiaohongshu.com/api/cas/customer/web/service-ticket",
                        {
                            headers: {
                                "x-s": XS,
                                "x-t": Date.now().toString(),
                                referer: "https://creator.xiaohongshu.com/",
                                origin: "https://creator.xiaohongshu.com",
                                cookie:
                                    (await task._steelBrowserContext?.cookies())
                                        ?.map((c: any) => `${c.name}=${c.value}`)
                                        .join(";") || "",
                            },
                            data: {
                                service: "https://creator.xiaohongshu.com",
                                source: "official",
                                type: "tgt",
                            },
                        },
                    );
                    task.logger.info(`createrLogin------- ${createrLogin.ok()}`);
                    if (createrLogin.ok()) {
                        let num = 30;
                        //轮询获取用户信息，直到获取到用户信息
                        async function getUserInfo() {
                            userInfo = await page.evaluate(() => {
                                const raw = (
                                    window as unknown as {
                                        __INITIAL_STATE__?: InitialState;
                                    }
                                ).__INITIAL_STATE__?.user?.userInfo?._rawValue;

                                return {
                                    name: raw?.nickname ?? raw?.nickname ?? "",
                                    avatar: raw?.images ?? raw?.images ?? "",
                                    uniqueId: raw?.userId ?? raw?.userId ?? "",
                                };
                            });
                            await reporter(0, `第${30 - num + 1}次抓取用户信息!`, { userInfo });
                            // 轮询获取用户信息，直到获取到用户信息
                            if (userInfo.uniqueId || num <= 0) return;
                            num--;
                            await new Promise((resolve) => setTimeout(resolve, 1000));
                            await getUserInfo();
                        }
                        await getUserInfo();
                        task.logger.info(`userInfo------- ${JSON.stringify(userInfo)}`);
                    }
                    executionState = ExecutionState.SUCCESS;
                    resolve();
                }
            } catch (err) {
                cleanup();
                reject(err);
            }
        };

        // 0=用户未扫码，1=用户已扫码未确认,3=二维码过期
        const handleCheckQR = async (response: Response) => {
            try {
                const url = response.url();
                if (!url.includes("/login/qrcode/status") || response.request().method() !== "GET") return;

                let jsonResponse: { code: number; data: { code_status: number; login_info: { user_id: string } } };
                try {
                    const text = await response.text(); // 总是安全的
                    if (!text) {
                        task.logger.warn("Empty response body:", { url });
                        return;
                    }

                    jsonResponse = JSON.parse(text); // 手动解析
                } catch (e) {
                    task.logger.warn("Failed to parse json:", { url, erroe: e });
                    return;
                }
                const codeStatus = jsonResponse.data?.code_status;

                // codeStatus ： 1为小红书扫码成功，2为微信扫码成功，并且同时返回登陆信息
                if (jsonResponse.code === 0 && (codeStatus === 1 || codeStatus === 2)) {
                    if (codeStatus === 2) userInfo.uniqueId = jsonResponse.data.login_info.user_id;

                    await updateTaskState?.({
                        state: TaskState.SCANNED,
                        result: {
                            userInfo,
                        },
                    });

                    page.off("response", handleCheckQR);
                    page.on("response", handleWebCommon);
                }
            } catch (err) {
                cleanup();
                reject(err);
            }
        };

        // 在页面加载之前就注册监听，防止丢失请求
        task.steelBrowser?.on("disconnected", handleBrowserClosed);
        page.on("response", handleCheckQR);
    });

    if (executionState === undefined) return { code: 500, message: "未知错误", data: {} };

    switch (executionState) {
        case ExecutionState.BROWSER_CLOSED: {
            const msg = "浏览器已关闭，任务中断!";
            await updateTaskState?.({
                state: TaskState.FAILED,
                error: "浏览器已关闭",
            });
            return { code: 414, message: msg, data: {} };
        }

        case ExecutionState.TIMEOUT: {
            const msg = "等待用户扫码登录超时!";
            await updateTaskState?.({
                state: TaskState.TIMEOUT,
                error: msg,
            });
            task.logger.info(msg);
            return { code: 414, message: msg, data: {} };
        }

        case ExecutionState.SUCCESS: {
            // 抓取 cookie，并尝试确保关键的 a1 设备指纹 cookie 已落地。
            // 若补救后仍缺 a1，记录日志但不阻断登录，留待后续 session 检测时拦截。
            const cookies = await collectCookiesWithA1(task);
            const hasA1 = cookies.some((c) => c.name === "a1" && !!c.value);

            if (!hasA1) {
                task.logger?.warn("[XhsLogin] 登录成功但缺失关键 cookie a1，后续检测可能失败", {
                    cookieNames: cookies.map((c) => c.name),
                });
            }

            const cookieStr = JSON.stringify(cookies);
            await updateTaskState?.({
                state: TaskState.SUCCESS,
                result: {
                    cookie: cookieStr,
                    userInfo: userInfo,
                },
            });
            return {
                code: 0,
                message: "成功",
                data: {
                    cookie: cookieStr,
                    userInfo,
                },
            };
        }
        default:
            // 处理 RUNNING 和其他可能的状态
            return { code: 500, message: "任务执行中断", data: {} };
    }
};
